import type { AbiParameter } from 'viem';
import type { TransactionBuilderParameterState } from '../transaction-builder/parameter-inputs/types';
import { getValueTypeFromSolidityType } from '../transaction-builder/parameter-inputs/utils';
import type {
  ParamTreeNode,
  ParameterParsedValue,
  RawParamValue,
} from '@mls-wallet/shared-types';

export interface DecodedParameterNode {
  name: string;
  type: string;
  value?: RawParamValue;
  children?: DecodedParameterNode[];
  /**
   * Decoded internal transactions from this parameter's bytes data
   * (used for multisend, delegate calls, etc. - future implementation)
   */
  valueDecoded?: unknown[];
}

/**
 * Convert a parsed value into a raw param value (stringified primitives or nested arrays)
 */
function toRawParamValue(parsed: ParameterParsedValue): RawParamValue {
  if (Array.isArray(parsed)) {
    return parsed.map((item) => toRawParamValue(item));
  }
  return parsed as unknown as RawParamValue;
}

/**
 * Build a decoded parameter tree for display, preserving names and nested components.
 * Mirrors self-custody's getDecodedParameterArrayForInternalTransaction for single-tx.
 */
export function decodeParametersForDisplay(
  functionInputs: AbiParameter[],
  params: TransactionBuilderParameterState[]
): DecodedParameterNode[] {
  if (!functionInputs || !params) return [];

  const buildNode = (
    abiParam: AbiParameter,
    value: ParameterParsedValue | undefined,
    pathIndex: number
  ): DecodedParameterNode => {
    if (value === undefined) {
      throw new Error(
        `Function param parsedValue not defined at index ${pathIndex}`
      );
    }

    // Tuple
    if (
      abiParam.type === 'tuple' &&
      'components' in abiParam &&
      abiParam.components
    ) {
      const tupleChildren = abiParam.components.map((comp, compIdx) => {
        const tupleValue =
          Array.isArray(value) && value.length > compIdx
            ? (value as ParameterParsedValue[])[compIdx]
            : undefined;
        return buildNode(comp as AbiParameter, tupleValue, compIdx);
      });

      return {
        name: abiParam.name || `param${pathIndex}`,
        type: abiParam.type,
        children: tupleChildren,
      };
    }

    // Array (including tuple arrays, both dynamic and static)
    // Matches: address[], uint256[], tuple[], tuple[2], etc.
    const arrayMatch = abiParam.type.match(/^(.+)\[(\d+)?\]$/);
    if (arrayMatch && Array.isArray(value)) {
      const baseType = arrayMatch[1]; // e.g., "address", "uint256", "tuple"
      const isTupleArray =
        baseType === 'tuple' && 'components' in abiParam && abiParam.components;
      const tupleComponents =
        isTupleArray && abiParam.components ? abiParam.components : undefined;

      const children = (value as ParameterParsedValue[]).map((item, idx) =>
        buildNode(
          {
            ...abiParam,
            name: `Item ${idx + 1}`, // Override name for array elements to show "Item 1", "Item 2", etc.
            type: baseType,
            ...(tupleComponents ? { components: tupleComponents } : {}),
          } as AbiParameter,
          item,
          idx
        )
      );
      return {
        name: abiParam.name || `param${pathIndex}`,
        type: abiParam.type,
        children,
      };
    }

    // Primitive
    return {
      name: abiParam.name || `param${pathIndex}`,
      type: abiParam.type,
      value: toRawParamValue(value),
    };
  };

  return functionInputs.map((param, index) =>
    buildNode(param, params[index]?.parsedValue, index)
  );
}

/**
 * Convert DecodedParameterNode to ParamTreeNode (for RawValue component)
 * This enables graceful fallback when displaying parameters that may fail to decode
 */
export function decodedNodeToParamTree(
  node: DecodedParameterNode
): ParamTreeNode {
  // For arrays and structs, rawValue should be undefined (they use children instead)
  // Convert all primitive values to strings for display
  const rawValue: string | undefined = (() => {
    if (node.value === undefined || node.value === null) return undefined;
    if (Array.isArray(node.value)) return undefined; // Arrays use children
    // Convert all primitives to string
    return String(node.value);
  })();

  const result: ParamTreeNode = {
    highLevelType: getValueTypeFromSolidityType(node.type),
    structParamName: node.name,
    rawType: node.type,
    rawValue: rawValue ?? '',
  };

  // Only add children property if they exist and are non-empty
  if (node.children && node.children.length > 0) {
    result.children = node.children.map(decodedNodeToParamTree);
  }

  return result;
}
